import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectUserProjectId } from 'core/userContext';
import { TemplateEditViews } from 'modules/templateEditor/common';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import InStudioTemplateEditor, {
  ConsumingApp,
} from 'modules/templateEditor/integrations/InStudioTemplateEditing';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';

import {
  getIsSMSLinkShorteningEnabled,
  useCampaignContext,
  useTemplateContext,
} from '../../context';
import { useCopyTemplateToCampaign } from '../../hooks';
import { CampaignContentCard } from '../CampaignCard';
import { getIsDefaultWhatsAppTemplate } from '../utils';
import { NewTemplate } from './NewTemplate';
import { NoTemplateContent } from './NoTemplateContent';
import { CampaignTemplateWrapper } from './TemplateContent.styles';
import { TemplateLoading } from './TemplateLoading';
import { TemplatePreview } from './TemplatePreview';

export const getContent = (
  isDefaultTemplate: boolean,
  templateAndContent: Record<string, any> | null,
  isLoading: boolean,
  isDefaultWhatsAppTemplate: boolean,
) => {
  if (isLoading) return TemplateLoading;
  if (!templateAndContent) return NoTemplateContent;
  if (isDefaultTemplate || isDefaultWhatsAppTemplate) {
    return NewTemplate;
  }
  return TemplatePreview;
};

export const TemplateContent = () => {
  const { setLocale } = useQueryParamContext();
  const { copyTemplateToCampaign } = useCopyTemplateToCampaign();
  const {
    campaign: { id, name, messageMedium, campaignTypeDefinition, templateId, placementId },
  } = useCampaignContext();
  const {
    templateState: { isDefaultTemplate },
    templateAndContent,
    refetch: refetchTemplate,
    isLoading,
    setIsSMSLinkShorteningEnabled,
  } = useTemplateContext();

  const { templateType } = templateAndContent;
  const isDefaultWhatsAppTemplate = getIsDefaultWhatsAppTemplate(messageMedium, templateType);

  const takeover = useTemplateTakeover();

  const projectId = useSelector(selectUserProjectId);

  const handleAttachment = (
    template: { id: number; locale: string },
    dataFeedId: number | null,
  ) => {
    copyTemplateToCampaign(
      { campaignId: id, templateId: template.id, dataFeedId },
      () => {
        setLocale(template.locale || '');
        takeover.setView(takeover.viewOptions.settings);
      },
      () => {
        takeover.close();
      },
    );
  };

  const handleClose = () => {
    /** We want to update the current value for Shorten and track stored in local storage */
    const isSMSLinkEnabled = getIsSMSLinkShorteningEnabled(projectId);
    setIsSMSLinkShorteningEnabled(isSMSLinkEnabled);

    refetchTemplate();
    takeover.close();
  };

  const TemplateContentComponent = useMemo(
    () =>
      getContent(
        isDefaultTemplate,
        templateAndContent,
        isLoading || (takeover.isOpen as boolean),
        isDefaultWhatsAppTemplate,
      ), // when the takeover is open, hide the preview and let it reload once it's closes
    [isDefaultTemplate, templateAndContent, isLoading, takeover.isOpen, isDefaultWhatsAppTemplate],
  );

  return (
    <CampaignTemplateWrapper>
      <CampaignContentCard $isTemplateContent>
        {takeover.isOpen ? (
          <InStudioTemplateEditor
            attachedTemplateId={templateId}
            campaignId={id}
            consumingApp={ConsumingApp.CAMPAIGN_EDITOR}
            templateId={takeover.view === TemplateEditViews.select ? 0 : templateId} // this keeps the preview from rendering within the takeover
            title={takeover.view === TemplateEditViews.select ? 'Attach template' : name}
            messageMedium={messageMedium}
            campaignType={campaignTypeDefinition}
            view={takeover.view}
            onViewChange={takeover.setView}
            onClose={handleClose}
            onAttachTemplate={handleAttachment}
            placementId={placementId}
          />
        ) : null}
        <TemplateContentComponent />
      </CampaignContentCard>
    </CampaignTemplateWrapper>
  );
};
